import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { changePasswordInput } from "@alm/validation";
import { TextField } from "@/components/app/fields/form-fields";
import { CellAlertsCard } from "@/components/app/cell-alerts-card";
import { LicenseCard } from "@/components/app/workforce/license-card";
import { PageHeader } from "@/components/app/shell/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { orpc } from "@/lib/orpc";
import { switchSession } from "@/lib/session/session";
import { usePolicy } from "@/lib/policy";
import { AccessBuildings } from "./-components/account/access-buildings";
import { AgentChatAlertsCard } from "./-components/integrations/agent-chat-alerts-card";
import { WebAlertsCard } from "./-components/integrations/web-alerts-card";
import { AccountCard } from "./-components/account/account-card";
import { AssistantMemoryCard } from "./-components/integrations/assistant-memory-card";
import { DeleteAccountCard } from "./-components/account/delete-account-card";
import { McpTokensCard } from "./-components/integrations/mcp-tokens-card";
import { OtherDevicesCard } from "./-components/kiosk/other-devices-card";
import { ProfileDetailsForm } from "./-components/account/profile-details-form";
import { QuickSigninCard } from "./-components/kiosk/quick-signin-card";
import { SiriShortcutCard } from "./-components/integrations/siri-shortcut-card";
import { TwoFactorCard } from "./-components/account/two-factor-card";

export const Route = createFileRoute("/_authenticated/_administration/settings/profile")({
  component: ProfileSettingsPage,
});

type PasswordValues = z.input<typeof changePasswordInput>;

function ProfileSettingsPage() {
  const { me, subject } = usePolicy();
  const queryClient = useQueryClient();
  const form = useForm<PasswordValues>({
    resolver: zodResolver(changePasswordInput),
    defaultValues: { currentPassword: "", newPassword: "" },
  });
  const change = useMutation(
    orpc.auth.changePassword.mutationOptions({
      onSuccess: () => {
        toast.success("Password changed");
        form.reset();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const leave = useMutation(
    orpc.administration.organizations.enter.mutationOptions({
      onSuccess: () => switchSession(queryClient, "/settings/plans-modules"),
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  return (
    <>
      <PageHeader
        title="My profile"
        meta="Your own details, password and sign-in shortcuts. Only you can change them."
      />
      <AccountCard />
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <ProfileDetailsForm key={me.user.id} user={me.user} />
        <Card>
          <CardHeader>
            <CardTitle>Access</CardTitle>
            <CardDescription>
              What you can reach, and where. Changing any of it is an administrator&rsquo;s job.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {subject.platformAdmin ? <Badge>Platform admin</Badge> : null}
              {subject.organization ? (
                <Badge variant="outline">
                  {subject.organization.role}, {me.activeOrganization?.name}
                </Badge>
              ) : null}
              <AccessBuildings subject={subject} listed={me.buildings} />
            </div>
            {subject.platformAdmin && subject.organization ? (
              <Button
                variant="outline"
                size="sm"
                disabled={leave.isPending}
                onClick={() => leave.mutate({ organizationId: null })}
              >
                {leave.isPending ? "Leaving…" : "Leave organization"}
              </Button>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
            <CardDescription>
              Other sessions are signed out when the password changes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((values) => change.mutate(values))}
                className="space-y-4"
              >
                <TextField
                  control={form.control}
                  name="currentPassword"
                  label="Current password"
                  type="password"
                  autoComplete="current-password"
                />
                <TextField
                  control={form.control}
                  name="newPassword"
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                />
                <Button type="submit" disabled={change.isPending}>
                  {change.isPending ? "Changing password…" : "Change password"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        <OtherDevicesCard />
        <AgentChatAlertsCard />
        <CellAlertsCard onProfile />
        <WebAlertsCard />
        <TwoFactorCard />
        <QuickSigninCard />
        <div className="lg:col-span-2">
          <LicenseCard userId={me.user.id} canEdit />
        </div>
        <SiriShortcutCard />
        <AssistantMemoryCard />
        <McpTokensCard />
        <DeleteAccountCard />
      </div>
    </>
  );
}
